import type { TokenPair } from "@/types/types";

const ACCESS = "sl.access";
const REFRESH = "sl.refresh";

export function getAccess(): string | null { return localStorage.getItem(ACCESS); }
export function getRefresh(): string | null { return localStorage.getItem(REFRESH); }
export function setTokens(p: TokenPair) {
  localStorage.setItem(ACCESS, p.access_token);
  localStorage.setItem(REFRESH, p.refresh_token);
}
export function clearTokens() { localStorage.removeItem(ACCESS); localStorage.removeItem(REFRESH); }

let refreshing: Promise<string | null> | null = null;

// A stale connection reused after the tab sat idle can leave this fetch
// hanging with no error — without the abort timeout, `refreshing` would
// stay set to that dangling promise forever, and every subsequent 401
// across the whole app would await the same promise and hang right along
// with it (only a hard reload resets the module state). Bound it instead.
async function doRefresh(): Promise<string | null> {
  const rt = getRefresh(); if (!rt) return null;
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 10000);
  try {
    const r = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
      signal: ctrl.signal,
    });
    if (!r.ok) { clearTokens(); return null; } // server actually rejected it
    const pair = (await r.json()) as TokenPair;
    setTokens(pair);
    return pair.access_token;
  } catch {
    // network error / stalled connection timing out — transient, don't
    // wipe out otherwise-valid tokens over it
    return null;
  } finally {
    window.clearTimeout(t);
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const token = getAccess();
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  if (res.status === 401 && retry) {
    refreshing = refreshing ?? doRefresh();
    const fresh = await refreshing;
    refreshing = null;
    if (fresh) return api<T>(path, init, false);
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const body = await res.text();
    let detail = body;
    try {
      const j = JSON.parse(body);
      const d = j?.detail;
      if (typeof d === "string") detail = d;
      else if (Array.isArray(d) && d[0]?.msg) detail = d.map((e) => e.msg).join(", ");
    } catch { /* body wasn't json, keep raw */ }
    throw new ApiError(res.status, body, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, public body: string, message?: string) {
    super(message || `Erreur ${status}`);
  }
}
export class UnauthorizedError extends ApiError {
  constructor() { super(401, "unauthorized", "Session expirée"); }
}
